import { useContext } from 'react';
import PropTypes from 'prop-types';
import i18nContext from 'components/i18n-context';
import { getConfirmationActionType } from 'services/authUtils';
import { amountFormattedValue, getErrorMessageForAlert } from 'services/utils';
import Alert from 'uikit/Alert/Alert';
import Button from 'uikit/Button/Button';
import './NewPaymentConfirmationTab.scss';

const NewPaymentConfirmationTab = ({
  isLoading,
  isCryptoTransaction,
  transferData,
  amountDetails,
  commissionDetails,
  totalCommissionDetails,
  onConfirm,
  error,
  handleEditTransferButtonClick,
  paymentPurposes
}) => {
  const i18n = useContext(i18nContext);

  const handleConfirm = () => {
    if (getConfirmationActionType()) {
      onConfirm();
    }
  };

  const getTransferDetailsFields = () => {
    if (isCryptoTransaction) {
      return [
        {
          label: i18n.getMessage('transfer.confirmation.label.senderName'),
          value: transferData.sender_name
        },
        {
          label: i18n.getMessage('transfer.confirmation.label.senderWalletNumber'),
          value: transferData?.sender_wallet_number
        },
        {
          label: i18n.getMessage('transfer.confirmation.label.senderWalletAddress'),
          value: transferData?.crypto_address
        },
        {
          label: i18n.getMessage('transfer.confirmation.label.recipientWalletAddress'),
          value: transferData?.counterparty?.crypto_wallet
        },
        {
          label: i18n.getMessage('transfer.confirmation.label.amount'),
          value:
            amountDetails?.displayAmount ||
            `${amountFormattedValue(transferData.amount, 2)} ${transferData.currency_code}`
        },
        {
          label: i18n.getMessage('transfer.confirmation.label.commission'),
          // eslint-disable-next-line max-len
          value:
            totalCommissionDetails?.displayAmount ||
            `${amountFormattedValue(transferData.total_commissions, 2)} ${transferData.currency_code}`
        },
        {
          label: i18n.getMessage('transfer.confirmation.label.paymentMethod'),
          value: i18n.getMessage(`transfer.paymentMethod.${transferData.payment_method}`)
        }
      ].filter((field) => field.value || field.value === 0);
    }

    return [
      {
        label: i18n.getMessage('transfer.confirmation.label.senderName'),
        value: transferData.sender_name
      },
      {
        label: i18n.getMessage('transfer.confirmation.label.senderAccount'),
        value: transferData.iban
      },
      {
        label: i18n.getMessage('transfer.confirmation.label.recipientName'),
        value: transferData.counterparty.party_name
      },
      {
        label: i18n.getMessage('transfer.confirmation.label.recipientAccount'),
        value: transferData.counterparty.iban
      },
      {
        label: i18n.getMessage('transfer.confirmation.label.recipientBankCountry'),
        value: transferData.counterparty.bank_country
      },
      {
        label: i18n.getMessage('transfer.confirmation.label.recipientBankName'),
        value: transferData.counterparty.bank_name
      },
      {
        label: i18n.getMessage('transfer.confirmation.label.recipientBic'),
        value: transferData.counterparty.bic
      },
      {
        label: i18n.getMessage('transfer.confirmation.label.recipientAccountNumber'),
        value: transferData.counterparty.account_number
      },
      {
        label: i18n.getMessage('transfer.confirmation.label.recipientSortCode'),
        value: transferData.counterparty.sort_code
      },
      {
        label: i18n.getMessage('transfer.confirmation.label.registrationNumber'),
        value: transferData.counterparty?.registration_number
      },
      {
        label: i18n.getMessage('transfer.confirmation.label.recipientAddress'),
        value: transferData.counterparty?.party_address
      },
      {
        label: i18n.getMessage('transfer.confirmation.label.recipientCity'),
        value: transferData.counterparty?.party_city
      },
      {
        label: i18n.getMessage('transfer.confirmation.label.recipientCountry'),
        value: transferData.counterparty?.party_country
      },
      {
        label: i18n.getMessage('transfer.confirmation.label.amount'),
        value:
          amountDetails?.displayAmount || `${amountFormattedValue(transferData.amount)} ${transferData.currency_code}`
      },
      {
        label: i18n.getMessage('transfer.confirmation.label.commission'),
        value:
          totalCommissionDetails?.displayAmount ||
          `${amountFormattedValue(transferData.total_commissions)} ${transferData.currency_code}`
      },
      {
        label: i18n.getMessage('transfer.confirmation.label.commentToTransfer'),
        value: transferData.comment
      },
      {
        label: i18n.getMessage('transfer.confirmation.label.paymentMethod'),
        value: i18n.getMessage(`transfer.paymentMethod.${transferData.payment_method}`)
      },
      {
        label: i18n.getMessage('transfer.confirmation.label.paymentReason'),
        value: (() => {
          const purposeKey = transferData.purpose || transferData.payment_purpose;
          if (purposeKey && paymentPurposes) {
            const purposeValue = paymentPurposes[purposeKey];
            return purposeValue ? `${purposeKey} - ${purposeValue}` : purposeKey;
          }
          return purposeKey;
        })()
      },
      {
        label: i18n.getMessage('transfer.confirmation.label.files'),
        value: transferData.uploadedFiles?.map((file) => file.name).join(',\n ')
      }
    ].filter((field) => field.value || field.value === 0);
  };

  const renderDetailRow = (label, value, index) => {
    if (!value) return null;

    return (
      <div className={'confirmation-detail-row'} key={`${label}-${index}`}>
        <div className={'confirmation-detail-label'}>{label}</div>
        <div className={'confirmation-detail-value'}>{value}</div>
      </div>
    );
  };

  return (
    <div className={'new-payment-confirmation'}>
      <div className={'confirmation-details'}>
        {getTransferDetailsFields().map((field, index) => renderDetailRow(field.label, field.value, index))}
      </div>

      <Alert type={'warning'} className={'confirmation-alert'} message={getErrorMessageForAlert(i18n, error)} />

      <div className={'confirmation-actions'}>
        <Button
          type={'outline'}
          size={'large'}
          fullWidth={true}
          onClick={handleEditTransferButtonClick}
          isDisabled={isLoading}
        >
          {i18n.getMessage('sendMoney.button.editTransfer')}
        </Button>
        <Button type={'primary'} size={'large'} fullWidth={true} onClick={handleConfirm} isDisabled={isLoading}>
          {i18n.getMessage('sendMoney.button.confirm')}
        </Button>
      </div>
    </div>
  );
};

NewPaymentConfirmationTab.propTypes = {
  isLoading: PropTypes.bool,
  isCryptoTransaction: PropTypes.bool,
  transferData: PropTypes.object,
  amountDetails: PropTypes.object,
  commissionDetails: PropTypes.object,
  totalCommissionDetails: PropTypes.object,
  onConfirm: PropTypes.func.isRequired,
  error: PropTypes.any,
  handleEditTransferButtonClick: PropTypes.func,
  paymentPurposes: PropTypes.object
};

export default NewPaymentConfirmationTab;
